import { type ClassValue, clsx } from 'clsx';
import { DateTime } from 'luxon';
import { twMerge } from 'tailwind-merge';
import { HistoryItemTitlePartType, HistoryItemType, MappedHistoryItem } from 'types/AssistantTypes';
import { ConstraintConfigType } from 'types/ConstraintsTypes';
import i18n from 'i18n/i18n';
import { monthAbbreviations } from 'shared/constants/dates';
import { formatLocalizedDate } from './dateUtils';
import { escapeRegExp } from 'lodash';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export const pctFormat = new Intl.NumberFormat('en-US', {
  style: 'percent',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
  signDisplay: 'always', // 'always' to display + for positive numbers
});

export enum Loading {
  Idle = 'IDLE',
  InProgress = 'IN_PROGRESS',
  Finished = 'FINISHED',
}

//*********** */

export type DateRange =
  | 'today'
  | 'yesterday'
  | 'thisWeek'
  | 'lastWeek'
  | 'thisMonth'
  | 'lastMonth'
  | 'thisYear'
  | 'lastYear'
  | 'remaining';

export const mapByDateRanges = (threads: MappedHistoryItem[]): Record<DateRange, MappedHistoryItem[]> => {
  const result: Record<DateRange, MappedHistoryItem[]> = {
    today: [],
    yesterday: [],
    thisWeek: [],
    lastWeek: [],
    thisMonth: [],
    lastMonth: [],
    thisYear: [],
    lastYear: [],
    remaining: [],
  };

  const now = DateTime.now();
  const startOfToday = now.startOf('day');
  const startOfYesterday = startOfToday.minus({ days: 1 });
  const startOfWeek = now.startOf('week');
  const startOfLastWeek = startOfWeek.minus({ weeks: 1 });
  const startOfMonth = now.startOf('month');
  const startOfLastMonth = startOfMonth.minus({ months: 1 });
  const startOfYear = now.startOf('year');
  const startOfLastYear = startOfYear.minus({ years: 1 });

  for (const thread of threads) {
    const updated = DateTime.fromISO(thread.updated_at);

    if (updated >= startOfToday) {
      result.today.push(thread);
    } else if (updated >= startOfYesterday && updated < startOfToday) {
      result.yesterday.push(thread);
    } else if (updated >= startOfWeek && updated < startOfYesterday) {
      result.thisWeek.push(thread);
    } else if (updated >= startOfLastWeek && updated < startOfWeek) {
      result.lastWeek.push(thread);
    } else if (updated >= startOfMonth && updated < startOfLastWeek) {
      result.thisMonth.push(thread);
    } else if (updated >= startOfLastMonth && updated < startOfMonth) {
      result.lastMonth.push(thread);
    } else if (updated >= startOfYear && updated < startOfLastMonth) {
      result.thisYear.push(thread);
    } else if (updated >= startOfLastYear && updated < startOfYear) {
      result.lastYear.push(thread);
    } else {
      result.remaining.push(thread);
    }
  }

  return result;
};

export const isEmptyObject = (obj: any) => {
  return Object.keys(obj).length === 0;
};
export function isEqual(obj1: any, obj2: any, checkLength: boolean = true): boolean {
  if (obj1 === obj2) return true;
  if (!obj1 || !obj2) return false;

  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);

  if (checkLength) {
    if (keys1.length !== keys2.length) {
      return true;
    }
  }

  if (keys1.length !== keys2.length) return false;

  for (const key of keys1) {
    const val1 = obj1[key];
    const val2 = obj2[key];

    if (typeof val1 === 'object' && typeof val2 === 'object') {
      if (!isEqual(val1, val2)) return false;
    } else if (val1 !== val2) {
      return false;
    }
  }
  return true;
}

export const convertToPercentage = (value: number) => {
  return (value * 100).toFixed(2);
};
export const getValueAndPercentage = (value: string) => {
  return value + ' %';
};
export const parseValueProduct = (targetObject: any) => {
  let inputValue = targetObject.value;
  inputValue = inputValue.replace(',', '.');

  // Remove any non-numeric characters (this includes letters)
  inputValue = inputValue.replace(/[^0-9.]/g, ''); // Remove anything that isn't a digit or dot

  // Handle validation for floats or percentages: allow one dot
  if (!/^\d*\.?\d*$/.test(inputValue)) {
    return inputValue; // Allow partial input like "1." or "1.2"
  }

  // Convert the string to a number if it's valid
  let value = parseFloat(inputValue);

  // If the value is not a number, return the string (e.g., during partial input)
  if (isNaN(value) && inputValue !== '') {
    return inputValue; // Keep the raw string if user is still typing
  }

  return inputValue; // Return the validated string during input
};
export const parseValue = (targetObject: any, data: any) => {
  let inputValue = targetObject.value;

  const inputType = data[targetObject.id].type;

  // Replace commas with dots for decimal support (only for float or percentage types)
  if (inputType === 'float' || inputType === 'percentage') {
    inputValue = inputValue.replace(',', '.'); // Convert comma to dot
  }

  // Remove any non-numeric characters (this includes letters)
  inputValue = inputValue.replace(/[^0-9.]/g, ''); // Remove anything that isn't a digit or dot

  // Handle validation for integers: no dots allowed
  if (inputType === 'int') {
    // Remove any dots if present (for integers)
    inputValue = inputValue.replace(/[.]/g, '');

    // Ensure only digits are present
    if (!/^\d*$/.test(inputValue)) {
      return ''; // Return empty if invalid character typed
    }
  }

  // Handle validation for floats or percentages: allow one dot
  if (inputType === 'float' || inputType === 'percentage') {
    // Ensure the input contains only digits and at most one dot
    if (!/^\d*\.?\d*$/.test(inputValue)) {
      return inputValue; // Allow partial input like "1." or "1.2"
    }
  }

  // Convert the string to a number if it's valid
  let value = parseFloat(inputValue);

  // If the value is not a number, return the string (e.g., during partial input)
  if (isNaN(value) && inputValue !== '') {
    return inputValue; // Keep the raw string if user is still typing
  }

  // Check for max value limits if provided
  if (data[targetObject.id]?.le) {
    let maxValue = data[targetObject.id].le;
    if (inputType === 'percentage') {
      maxValue = data[targetObject.id].le * 100;
    }
    if (value > maxValue) {
      inputValue = maxValue.toString();
    }
  }
  // Check for min value limits if provided
  //** fix for asset count */
  if (data[targetObject.id]?.ge && targetObject.id === 'asset_count_max') {
    return inputValue;
  }
  if (data[targetObject.id]?.ge && targetObject.id === 'asset_count_min') {
    return inputValue;
  }
  if (data[targetObject.id]?.ge) {
    let minValue = data[targetObject.id].ge;
    if (inputType === 'percentage') {
      minValue = data[targetObject.id].ge * 100;
    }
    if (value < minValue) {
      inputValue = minValue.toString();
    }
  }

  return inputValue; // Return the validated string during input
};

export const parseConstraintsFormValues = (
  formValues: Record<string, string | number>,
  configData: ConstraintConfigType,
) => {
  const convertedFormValues = {};
  for (let key in formValues) {
    const value = formValues[key];
    const type = configData[key].type;
    let valueConverted: any = value;
    if (typeof value === 'string') {
      if (value.length === 0) {
        valueConverted = null;
      } else {
        switch (type) {
          case 'boolean':
            break;
          case 'string':
            break;
          default:
            valueConverted = Number(value);
            if (type === 'percentage') {
              valueConverted = valueConverted / 100;
            }
        }
      }
    }
    convertedFormValues[key] = valueConverted;
  }
  return convertedFormValues;
};

const riskProfileMappingObject = {
  engToIta: {
    cautious: 'Cauto',
    prudent: 'Prudente',
    balanced: 'Bilanciato',
    dynamic: 'Dinamico',
  },
  itaToEng: {
    cauto: 'cautious',
    prudente: 'prudent',
    bilanciato: 'balanced',
    dinamico: 'dynamic',
  },
};
export const riskProfileMapEngToIta = (riskProfile: string) => {
  const riskProfileLC = riskProfile.toLowerCase();
  if (riskProfileMappingObject.engToIta[riskProfileLC]) {
    return riskProfileMappingObject.engToIta[riskProfileLC];
  }
  return riskProfile;
};
export const riskProfileMapItaToEng = (riskProfile: string) => {
  const riskProfileLC = riskProfile.toLowerCase();
  if (riskProfileMappingObject.itaToEng[riskProfileLC]) {
    return riskProfileMappingObject.itaToEng[riskProfileLC];
  }
  return riskProfile;
};

const esgPreferenceMappingObject = {
  engToIta: {
    no_preference: 'Nessuna Preferenza',
    low: 'Bassa',
    medium: 'Media',
    high: 'Alta',
  },
};
export const esgPreferenceMapEngToIta = (esgPreference: string) => {
  const esgPreferenceLC = esgPreference.toLowerCase();
  if (esgPreferenceMappingObject.engToIta[esgPreferenceLC]) {
    return esgPreferenceMappingObject.engToIta[esgPreferenceLC];
  }
  return esgPreference;
};
export const esgPreferenceMapItaToEng = (esgPreference: string) => {
  const esgPreferenceLC = esgPreference.toLowerCase();
  for (const [key, value] of Object.entries(esgPreferenceMappingObject.engToIta)) {
    if (value.toLowerCase() === esgPreferenceLC) {
      return key;
    }
  }
  return esgPreference;
};

export const findByProp = (arr, prop, value) => {
  const searchValue = typeof value === 'string' ? Number(value) : value;
  for (let i = 0; i < arr.length; i++) {
    if (arr[i][prop] === searchValue) return arr[i];
  }
  return null;
};

export const getTimeDiff = (date1: string, date2: string) => {
  const diff = Math.abs(new Date(date1).getTime() - new Date(date2).getTime()) / 1000;

  const days = Math.floor(diff / 86400);
  const hours = Math.floor((diff % 86400) / 3600);
  const mins = Math.floor((diff % 3600) / 60);
  const secs = Math.floor(diff % 60);

  return {
    days,
    hours,
    mins,
    secs,
    formatted: `${days ? days + 'd ' : ''}${hours ? hours + 'h ' : ''}${mins ? mins + 'm ' : ''}${secs}s`,
  };
};

export const formatTimeRange = (timeStart, timeEnd) => {
  // const start = new Date(timeStart).getUTCHours();
  const start = new Date(timeStart).getHours();
  // const end = new Date(timeEnd).getUTCHours();
  const end = new Date(timeEnd).getHours();
  return `${start}:00 - ${end}:00`;
};

export const mapPriority = {
  urgent: 'urgente',
  high: 'alto',
  medium: 'medio',
  low: 'basso',
};

export const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return formatLocalizedDate(date, i18n.language, 'MMM yyyy');
};

export const formatCurrency = (amount: string, currency = '€') => {
  const num = parseInt(amount);
  return `${currency} ${num.toLocaleString()}`;
};

export const calculateProgress = (current: string, total: string) => {
  return Math.round((parseInt(current) / parseInt(total)) * 100);
};

export const formatDateWithTime = (input) => {
  // Ensure we have a valid Date object
  let date;

  try {
    // If input is already a Date object
    if (input instanceof Date) {
      date = input;
    }
    // If input is a string or number
    else {
      date = new Date(input);
    }

    // Check if date is valid
    if (isNaN(date.getTime())) {
      throw new Error('Invalid date');
    }
  } catch (error) {
    console.error('Error parsing date:', error);
    return 'Invalid date format';
  }

  // Get components
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const month = monthAbbreviations[date.getMonth()];
  const day = date.getDate();
  const year = date.getFullYear();

  // Return formatted string
  return `${hours}:${minutes} - ${month} ${day}, ${year}`;
};
export const formatDateOnlyDate = (input) => {
  // Ensure we have a valid Date object
  let date;

  try {
    // If input is already a Date object
    if (input instanceof Date) {
      date = input;
    }
    // If input is a string or number
    else {
      date = new Date(input);
    }

    // Check if date is valid
    if (isNaN(date.getTime())) {
      throw new Error('Invalid date');
    }
  } catch (error) {
    console.error('Error parsing date:', error);
    return 'Invalid date format';
  }

  // Get components
  const month = monthAbbreviations[date.getMonth()];
  const day = date.getDate();
  const year = date.getFullYear();

  // Return formatted string
  return `${month} ${day}, ${year}`;
};

export const formatDateOnlyTime = (input) => {
  // Ensure we have a valid Date object
  let date;

  try {
    // If input is already a Date object
    if (input instanceof Date) {
      date = input;
    }
    // If input is a string or number
    else {
      date = new Date(input);
    }

    // Check if date is valid
    if (isNaN(date.getTime())) {
      throw new Error('Invalid date');
    }
  } catch (error) {
    console.error('Error parsing date:', error);
    return 'Invalid date format';
  }

  // Get components
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');

  // Return formatted string
  return `${hours}:${minutes}`;
};

export const getInitials = (firstName: string, lastName: string) => {
  return `${firstName?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase();
};
export const capitalizeFirstLetter = (string: string) => {
  return string.charAt(0).toUpperCase() + string.slice(1).toLowerCase();
};

export const getConstraintField = (field: string) => {
  return field.split('_min')[0];
};
export const removeLastFourChars = (str) => {
  return str.slice(0, -4);
};
export const reorderMaxMin = (acc: string[][]) => {
  return acc.map((pair) => {
    const minItem = pair.find((item) => item.endsWith('_min'));
    const maxItem = pair.find((item) => item.endsWith('_max'));

    const result = [];
    if (minItem) result.push(minItem);
    if (maxItem) result.push(maxItem);

    return result;
  });
};
// Debounce
// export function debounce(func, time) {
//   var time = time || 100; // 100 by default if no param
//   var timer;
//   return function (event) {
//     if (timer) clearTimeout(timer);
//     timer = setTimeout(func, time, event);
//   };
// }
export function debounce(func, delay = 100) {
  let timer; // scoped to the closure

  return function (...args) {
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => {
      func.apply(this, args); // preserve context and pass args
    }, delay);
  };
}

export function hexToRgba(hex, opacity) {
  const bigint = parseInt(hex.replace('#', ''), 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;

  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
}
export function parseMonths(shortMonth: string) {
  const shortMonths = {
    Gen: 'Gennaio',
    Feb: 'Febbraio',
    Mar: 'Marzo',
    Apr: 'Aprile',
    Mag: 'Maggio',
    Giu: 'Giugno',
    Lug: 'Luglio',
    Ago: 'Agosto',
    Set: 'Settembre',
    Ott: 'Ottobre',
    Nov: 'Novembre',
    Dic: 'Dicembre',
  };
  if (shortMonths[shortMonth]) {
    return shortMonths[shortMonth];
  }
  return shortMonth;
}

export const highlightSearchTermInTitle = (thread: HistoryItemType, searchTerm: string): MappedHistoryItem => {
  if (!searchTerm.trim()) return thread;
  const regex = new RegExp(`(${escapeRegExp(searchTerm)})`, 'gi');
  const parts = thread.title.split(regex);
  const titleParts: HistoryItemTitlePartType[] = parts
    .map((part) => {
      if (!part) return { text: '', highlight: false };
      if (regex.test(part)) {
        return {
          text: part,
          highlight: true,
        };
      }
      return {
        text: part,
        highlight: false,
      };
    })
    .filter((part) => part.text !== '');
  return {
    ...thread,
    title: titleParts,
  };
};
